/**
 * CelesteRightPseudoBox Component
 *
 * Celeste skin right pseudo box with sky-blue gradient surface, cloud decorations,
 * and red bottom accent. Displays player username with truncation.
 */

import { memo } from 'react';
import type { PseudoBoxProps } from '../basics/constants/types';
import { buildClassNames } from '@/utils/classNames';
import { formatPseudoForDisplay, PSEUDO_BOX_CONFIG } from '../basics/utils/utils';
import { CELESTE_PSEUDO_VIEWBOX } from './constants';

const CelesteRightPseudoBox = memo(function CelesteRightPseudoBox({
  pseudo,
  viewBox = CELESTE_PSEUDO_VIEWBOX,
  className = ''
}: PseudoBoxProps) {
  const svgClassName = buildClassNames(className);
  const displayText = formatPseudoForDisplay(pseudo);

  return (
    <svg
      viewBox={viewBox}
      className={svgClassName}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Player username display box - celeste right position"
      focusable="false"
    >
      <defs>
        {/* Dark bottom base */}
        <linearGradient
          id="crpDark"
          x1="5.72"
          y1="72.52"
          x2="303.12"
          y2="72.52"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#333333" />
          <stop offset="1" stopColor="#1A1A1A" />
        </linearGradient>
        {/* Red mid-layer accent */}
        <linearGradient
          id="crpRed"
          x1="5.72"
          y1="62.87"
          x2="303.15"
          y2="62.87"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#CE6161" />
          <stop offset="0.48" stopColor="#C14848" />
          <stop offset="1" stopColor="#BC2A2A" />
        </linearGradient>
        {/* Celeste surface gradient */}
        <linearGradient
          id="crpSurface"
          x1="154.34"
          y1="6.96"
          x2="154.34"
          y2="80.89"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#72BBFC" />
          <stop offset="0.48" stopColor="#A7DFFC" />
          <stop offset="1" stopColor="#E6FBFF" />
        </linearGradient>
        {/* Cloud gradients */}
        <linearGradient
          id="crpCloud1"
          x1="243.343"
          y1="72.96"
          x2="287.655"
          y2="54.38"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#72BBFC" />
          <stop offset="0.48" stopColor="#A7DFFC" />
          <stop offset="1" stopColor="#E6FBFF" />
        </linearGradient>
        <linearGradient
          id="crpCloud2"
          x1="138.27"
          y1="27.68"
          x2="29.87"
          y2="27.68"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#72BBFC" />
          <stop offset="0.48" stopColor="#A7DFFC" />
          <stop offset="1" stopColor="#E6FBFF" />
        </linearGradient>
        <linearGradient
          id="crpCloud3"
          x1="24.12"
          y1="37.43"
          x2="5.72"
          y2="37.43"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#72BBFC" />
          <stop offset="0.48" stopColor="#A7DFFC" />
          <stop offset="1" stopColor="#E6FBFF" />
        </linearGradient>
        {/* Text clip path */}
        <clipPath id="crpTextClip">
          <rect x="15" y="5" width="277" height="75" rx="20" />
        </clipPath>
      </defs>

      {/* Layer 1: Dark bottom base */}
      <path
        d="M303.12 79.3099C303.14 81.6499 302.34 83.9399 300.81 85.7099C300.8 85.7299 300.79 85.7399 300.78 85.7499C300.78 85.7499 297.01 90.7399 291.75 92.8599C285.32 95.4499 280.32 93.3499 272.54 93.2399C264.32 93.1299 261.17 95.3499 252.36 97.4099C239.63 100.4 236.77 97.9799 200.87 97.4099C200.02 97.3999 191.85 97.3899 175.5 97.3799C164.84 97.3699 149.92 97.3599 134.77 97.3799C113.7 97.4099 92.63 97.3699 71.56 97.3799C60.98 97.3799 50.42 95.8299 39.85 96.3199C31.19 96.7199 24.01 97.6399 16.41 93.6299C16.36 93.5999 16.31 93.5699 16.25 93.5399C9.69997 90.0199 5.71997 83.0799 5.71997 75.6299V50.1999C5.83997 50.2099 5.95997 50.2299 6.07997 50.2399C11.78 50.9399 17.59 51.5999 23.49 52.2299C58.59 55.9599 97.2 58.3399 138.84 58.3699C200.19 58.3999 255.32 53.2999 302.8 46.1699C302.84 50.0199 302.88 53.8699 302.91 57.7199V57.7599C302.96 62.8699 303.01 67.9899 303.06 73.0999C303.08 75.1699 303.1 77.2399 303.12 79.3099Z"
        fill="url(#crpDark)"
      />

      {/* Layer 2: Red mid-layer accent */}
      <path
        d="M303.15 72.96C303.15 72.96 303.09 73.05 303.06 73.1C302.53 73.89 301.78 74.95 300.78 76.1C300.78 76.1 297.01 81.09 291.75 83.21C285.32 85.8 280.32 83.7 272.54 83.59C264.32 83.48 261.17 85.7 252.36 87.77C239.63 90.75 236.77 88.33 200.87 87.77C200.02 87.75 191.85 87.75 175.5 87.73C164.84 87.72 149.92 87.71 134.77 87.73C113.7 87.76 92.63 87.73 71.56 87.73C60.98 87.73 50.42 86.18 39.85 86.67C31.19 87.07 24.01 88 16.41 83.98C11.14 81.19 7.75997 77.14 5.71997 74.12V40.56C35.72 44.23 68.54 47 103.84 48.14C110.25 48.35 116.75 48.51 123.33 48.6C128.45 48.68 133.62 48.72 138.83 48.72C200.18 48.76 255.31 43.65 302.79 36.52C302.84 41.73 302.89 46.94 302.94 52.14C303.01 59.08 303.07 66.02 303.14 72.96H303.15Z"
        fill="url(#crpRed)"
      />

      {/* Layer 3: Celeste surface */}
      <path
        d="M302.95 30.0699V56.3899C302.95 56.8499 302.94 57.3099 302.91 57.7599C302.2 70.7399 291.44 81.0499 278.28 81.0499H41.6C27.9 81.0499 15.99 73.3699 9.94997 62.0799C8.00997 58.4599 6.66997 54.4699 6.07997 50.2399C5.83997 48.5999 5.71997 46.9299 5.71997 45.2299V30.0699C5.71997 16.4499 16.76 5.40991 30.38 5.40991H278.28C291.9 5.40991 302.95 16.4499 302.95 30.0699Z"
        fill="url(#crpSurface)"
      />

      {/* Cloud decoration - right */}
      <path
        d="M243.96 73.4099C242.27 69.3799 245.53 64.1699 251.23 61.7799C252.41 61.2799 253.61 60.9499 254.78 60.7599C253.66 57.3199 255.68 53.3799 259.5 51.7799C262.62 50.4699 266.02 51.1099 268.07 53.1499C268.89 52.4899 269.85 51.9099 270.92 51.4599C275.49 49.5399 280.32 50.6999 281.72 54.0399C281.97 54.6499 282.1 55.2799 282.1 55.9299C285.93 55.7199 289.32 57.3199 290.57 60.2899C292.29 64.3899 289.29 69.5599 283.87 71.8299C280.66 73.1799 277.32 73.2099 274.78 72.1599C273.37 73.5799 271.49 74.8599 269.27 75.7799C264.99 77.5799 260.65 77.5899 258 76.0799C257.79 76.1799 257.58 76.2699 257.37 76.3599C251.66 78.7499 245.66 77.4299 243.97 73.3999L243.96 73.4099Z"
        fill="url(#crpCloud1)"
      />

      {/* Cloud decoration - left */}
      <path
        d="M138.27 31.7799C138.27 39.3899 132.1 45.8999 123.34 48.5999C120.54 49.4699 117.47 49.9499 114.26 49.9499C110.52 49.9499 106.99 49.3099 103.84 48.1399C100.36 46.8899 97.3599 45.0099 95.0599 42.6999C90.8799 44.4299 85.8599 45.4399 80.4399 45.4399C69.9699 45.4399 60.9299 41.6699 56.7299 36.2299C56.2099 36.2599 55.6899 36.2699 55.1599 36.2699C41.1999 36.2699 29.8699 28.2799 29.8699 18.4199C29.8699 13.2899 32.9399 8.65991 37.8499 5.40991H123.78C124.77 7.14991 125.32 9.05991 125.32 11.0599C125.32 12.5399 125.02 13.9699 124.47 15.3199C132.63 18.2299 138.27 24.5099 138.27 31.7799Z"
        fill="url(#crpCloud2)"
      />

      {/* Cloud decoration - small left */}
      <path
        d="M17.94 44.8298C15.35 45.6798 12.75 45.4998 10.83 44.5198C10.56 44.7598 10.26 44.9898 9.94997 45.1998C8.91997 45.9398 7.68997 46.5598 6.30997 47.0098C6.10997 47.0698 5.91997 47.1298 5.71997 47.1898V28.3598C6.11997 28.6098 6.46997 28.9298 6.77997 29.2898C7.45997 28.8198 8.24997 28.4298 9.09997 28.1498C12.78 26.9398 16.48 28.1398 17.37 30.8298C17.53 31.3198 17.58 31.8198 17.55 32.3198C20.16 32.3898 22.42 33.4898 23.49 35.3598C23.64 35.6398 23.78 35.9398 23.88 36.2498C24.96 39.5498 22.31 43.3898 17.94 44.8298Z"
        fill="url(#crpCloud3)"
      />

      {/* Dynamic player pseudo text */}
      <g clipPath="url(#crpTextClip)">
        <text
          x={PSEUDO_BOX_CONFIG.TEXT.RIGHT_X_POSITION}
          y={PSEUDO_BOX_CONFIG.TEXT.Y_POSITION}
          fill="#FFFFFF"
          fontSize={PSEUDO_BOX_CONFIG.TEXT.FONT_SIZE}
          fontWeight="bold"
          textAnchor="end"
          dominantBaseline="middle"
          stroke="#000000"
          strokeWidth="2"
          strokeLinejoin="round"
          paintOrder="stroke"
        >
          {displayText}
        </text>
      </g>
    </svg>
  );
});

export default CelesteRightPseudoBox;
